/**
 * Colour dropdown — thin open/close wrapper around the native swatch fieldset.
 *
 * The trigger displays the currently selected colour and is re-rendered
 * server-side by Liquid on every variant change, so morphdom keeps it in sync
 * automatically. No manual trigger sync needed here.
 *
 * Native radio inputs inside the fieldset are fully tracked by Shopify's
 * variant-picker.js — no changes to that file required.
 */
class ColourDropdown extends HTMLElement {
  #bound = false;

  #outsideClick = (e) => {
    if (!this.contains(e.target)) this.#close();
  };

  // Queried per use rather than cached: morphdom rewrites this subtree on every
  // variant change, and a cached node can end up detached.
  get #trigger() {
    return this.querySelector('.bw-colour-trigger');
  }

  get #panel() {
    return this.querySelector('.bw-colour-panel');
  }

  connectedCallback() {
    document.addEventListener('click', this.#outsideClick);

    // Listeners live on `this`, so a disconnect/reconnect (morphdom moving the
    // subtree) would otherwise stack a second set and toggle twice per click.
    if (this.#bound) return;
    this.#bound = true;

    this.addEventListener('click', (e) => {
      if (e.target.closest('.bw-colour-trigger')) this.#toggle();
    });

    this.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      if (this.#panel?.hidden) return;
      e.stopPropagation();
      this.#close();
      this.#trigger?.focus();
    });

    // Close the panel whenever any radio inside fires a change (colour selected)
    this.addEventListener('change', () => this.#close());
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#outsideClick);
  }

  #toggle() {
    this.#panel?.hidden ? this.#open() : this.#close();
  }

  #open() {
    const panel = this.#panel;
    if (!panel) return;

    panel.hidden = false;
    this.#trigger?.setAttribute('aria-expanded', 'true');

    // The panel scrolls (a full colour ring runs to 70 shades), so open it on
    // the current colour instead of at the top of the list.
    const checked = panel.querySelector('input:checked');
    const row = checked?.closest('li');
    if (row) panel.scrollTop = row.offsetTop - (panel.clientHeight - row.offsetHeight) / 2;
  }

  #close() {
    const panel = this.#panel;
    if (!panel || panel.hidden) return;

    panel.hidden = true;
    this.#trigger?.setAttribute('aria-expanded', 'false');
  }
}

if (!customElements.get('colour-dropdown')) {
  customElements.define('colour-dropdown', ColourDropdown);
}
